import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} TierConfig
 * @property {number} minQty
 * @property {string} key
 */

/**
 * Quantity-based delivery and order benefits (PDP + cart).
 */
class FyhOrderBenefits extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {number} */
  #cartItemCount = 0;
  /** @type {number} */
  #deliveryFee = 250;
  /** @type {number} */
  #discountAmount = 300;
  /** @type {string | null} */
  #lastGiftPackingValue = null;
  /** @type {boolean} */
  #useActualCartOnly = false;

  /** @type {TierConfig[]} */
  static #TIERS = [
    { minQty: 5, key: 'tier-5' },
    { minQty: 3, key: 'tier-3' },
    { minQty: 2, key: 'tier-2' },
    { minQty: 1, key: 'tier-1' },
  ];

  connectedCallback() {
    this.#abortController = new AbortController();
    this.#parseConfig();
    this.#attachListeners();
    this.#refreshDisplay();

    if (this.dataset.context === 'cart' && this.#cartItemCount >= 0) {
      this.#syncGiftPackingFromCount(this.#cartItemCount);
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  #parseConfig() {
    this.#cartItemCount = parseInt(this.dataset.cartItemCount || '0', 10) || 0;
    this.#deliveryFee = parseInt(this.dataset.deliveryFee || '250', 10) || 250;
    this.#discountAmount = parseInt(this.dataset.discountAmount || '300', 10) || 300;
  }

  #attachListeners() {
    const { signal } = this.#abortController ?? { signal: undefined };

    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });

    const quantityInput = this.#getQuantityInput();
    if (quantityInput) {
      quantityInput.addEventListener('input', this.#handleQuantityInput, { signal });
      quantityInput.addEventListener('change', this.#handleQuantityInput, { signal });
    }
  }

  #handleQuantityUpdate = (event) => {
    if (this.dataset.context !== 'pdp') return;

    const form = this.closest('product-form-component');
    if (!form || !(event.target instanceof Node) || !form.contains(event.target)) return;

    this.#useActualCartOnly = false;
    this.#refreshDisplay();
  };

  #handleQuantityInput = () => {
    if (this.dataset.context === 'pdp') {
      this.#useActualCartOnly = false;
      this.#refreshDisplay();
    }
  };

  #handleCartUpdate = async () => {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return;

      const cart = await response.json();
      this.#cartItemCount = cart.item_count || 0;
      this.dataset.cartItemCount = String(this.#cartItemCount);

      if (this.dataset.context === 'pdp') {
        this.#useActualCartOnly = true;
      }

      this.#refreshDisplay();
      await this.#syncGiftPackingAttribute(cart);
    } catch {
      /* ignore network errors */
    }
  };

  /**
   * @returns {HTMLInputElement | null}
   */
  #getQuantityInput() {
    if (this.dataset.context !== 'pdp') return null;
    const form = this.closest('product-form-component');
    return form?.querySelector('input[name="quantity"]') ?? null;
  }

  /**
   * @returns {number}
   */
  #getEffectiveQuantity() {
    if (this.dataset.context === 'cart' || this.#useActualCartOnly) {
      return this.#cartItemCount;
    }

    const quantityInput = this.#getQuantityInput();
    const inputQty = parseInt(quantityInput?.value || '1', 10) || 1;
    return this.#cartItemCount + inputQty;
  }

  /**
   * @param {number} qty
   * @returns {TierConfig}
   */
  #getActiveTier(qty) {
    return FyhOrderBenefits.#TIERS.find((tier) => qty >= tier.minQty) ?? FyhOrderBenefits.#TIERS.at(-1);
  }

  /**
   * @param {number} qty
   * @returns {string}
   */
  #getNudgeText(qty) {
    if (qty >= 5) {
      return `You unlocked complimentary gift packing, free delivery, and PKR ${this.#discountAmount} off your order.`;
    }
    if (qty === 4) {
      return `Add 1 more item to unlock PKR ${this.#discountAmount} off your order.`;
    }
    if (qty === 3) {
      return `Add 2 more items to unlock PKR ${this.#discountAmount} off your order.`;
    }
    if (qty === 2) {
      return 'Add 1 more item for complimentary gift packing.';
    }
    return 'Add 1 more item for free delivery.';
  }

  #refreshDisplay() {
    const qty = this.#getEffectiveQuantity();
    const activeTier = this.#getActiveTier(qty);

    const nudge = this.querySelector('[data-ref="nudge"]');
    if (nudge instanceof HTMLElement) {
      nudge.textContent = this.#getNudgeText(qty);
      nudge.hidden = qty >= 5 && qty > 0;
    }

    this.querySelectorAll('[data-tier]').forEach((row) => {
      if (!(row instanceof HTMLElement)) return;
      row.classList.toggle('fyh-order-benefits__row--active', row.dataset.tier === activeTier.key);
    });
  }

  /**
   * @param {{ item_count: number, attributes?: Record<string, string> }} cart
   */
  async #syncGiftPackingAttribute(cart) {
    await this.#syncGiftPackingFromCount(cart.item_count, cart.attributes?.gift_packing ?? '');
  }

  /**
   * @param {number} itemCount
   * @param {string} [currentValue]
   */
  async #syncGiftPackingFromCount(itemCount, currentValue = '') {
    if (this.dataset.context !== 'cart') return;

    const desired = itemCount >= 3 ? 'yes' : '';

    if (desired === currentValue || desired === this.#lastGiftPackingValue) {
      this.#lastGiftPackingValue = desired;
      return;
    }

    this.#lastGiftPackingValue = desired;

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            gift_packing: desired || null,
          },
        }),
      });
    } catch {
      this.#lastGiftPackingValue = null;
    }
  }
}

if (!customElements.get('fyh-order-benefits')) {
  customElements.define('fyh-order-benefits', FyhOrderBenefits);
}
